/**
 * setHistory — "Your sets · getting faster?" (Scores tab; spec item 3, the
 * speed thread, docs/superpowers/specs/2026-09-01-club-first-class-design.md).
 *
 * Pure. Input: the swimFacts v2 cache entries (one per swim, each carrying its
 * named sets — see allTimeAggregates.SwimFactsSet). Output: one row per setId
 * with first / fastest / last, the oldest→newest series the bars draw, and
 * the fastest length per stroke inside that set.
 *
 * Basis rule (spec): a set that is ALWAYS the same distance (The Rockalita,
 * 200 m every time) is measured by its whole-set time. A set whose distance
 * varies (Killer BS 100–200 m, Freestyle set 50–575 m) is measured by the
 * average counted length, so a 4-length day and an 8-length day compare.
 * The caption on the card says which one is in play; the number formats
 * differ (m:ss vs 37.1s) so the two can never be mistaken for each other.
 *
 * Tie rule: the EARLIEST swim keeps a fastest record (same as every tile on
 * the tab). Ordering: most-swum first, then newest last-swim first.
 *
 * HARD RULE (owner): every number here is one someone could recount by hand
 * from the set's lengths — no indices, no weighting.
 */
import type { SwimFactsEntry, SwimFactsSet, SwimFactsSetBlock } from "./allTimeAggregates";
import { medianDurationByStroke, recordFloorSeconds, type MedianByStroke } from "./recordEligibility";
import { SET_DISPLAY, inImOrder } from "./setLibrary";
import { fmtMSS, fmtSeconds1 } from "./format";

export type SetHistoryBasis = "setTime" | "perLength";

/** One stroke inside one swim of a set: what a stroke-colored bar is split by. */
export interface SetPointStroke {
  stroke: string;
  /** Counted lengths of this stroke in this occurrence (excluded laps dropped). */
  lengths: number;
  /** Counted seconds in this stroke: its lengths × its mean length time. */
  seconds: number;
}

export interface SetHistoryPoint {
  /** Whole-set seconds (basis setTime) or mean counted-length seconds (perLength). */
  valueS: number;
  /** Set start, epoch ms. */
  dateMs: number;
  workoutId: string;
  meters: number;
  /** True when a counted length inside this occurrence failed the record
   *  floor (a split-length glitch). Drawn and counted; never "fastest". */
  hasPhantom: boolean;
  /** In the order the strokes first appear in the set: extractSets fills
   *  perStroke lap by lap, and the JSON cache keeps string-key order. */
  strokes: SetPointStroke[];
  /** The occurrence's lengths in SWIM ORDER, run-length encoded (swimFacts v5).
   *  What the Sets tab card draws — `strokes` above is an aggregate and cannot
   *  say what order they were swum in. Empty for a set cached before v5, until
   *  the background rescan re-derives that swim. */
  blocks: SwimFactsSetBlock[];
}

export interface StrokeBest {
  stroke: string;
  /** Fastest counted length of this stroke inside the set, any swim. */
  seconds: number;
}

export interface SetHistoryRow {
  setId: string;
  displayName: string;
  /** Occurrences — two Rockalitas in one swim count twice (two bars). */
  timesSwum: number;
  /** Named club set (The Rockalita, Killer BS…) vs a generic shape (Freestyle set). */
  isSignature: boolean;
  basis: SetHistoryBasis;
  metersMin: number;
  metersMax: number;
  /** The one pool length every occurrence was swum in; null when it varied. */
  poolLengthMeters: number | null;
  first: SetHistoryPoint;
  fastest: SetHistoryPoint;
  last: SetHistoryPoint;
  /** Oldest → newest. */
  series: SetHistoryPoint[];
  /** IM order first (fly, back, breast, free), then anything else as first seen. */
  bestPerLengthByStroke: StrokeBest[];
}

interface Occurrence {
  set: SwimFactsSet;
  workoutId: string;
}

/** Mean counted-length seconds across the set's strokes; null when every lap was excluded. */
function meanCountedLength(set: SwimFactsSet): number | null {
  let total = 0;
  let n = 0;
  for (const s of Object.values(set.perStroke)) {
    total += s.meanS * s.n;
    n += s.n;
  }
  return n === 0 ? null : total / n;
}

function pointStrokes(set: SwimFactsSet): SetPointStroke[] {
  return Object.entries(set.perStroke).map(([stroke, s]) => ({
    stroke,
    lengths: s.n,
    seconds: s.meanS * s.n,
  }));
}

function buildRow(setId: string, occ: Occurrence[], medians: MedianByStroke): SetHistoryRow | null {
  const display = SET_DISPLAY[setId];
  if (!display) return null;

  const sorted = [...occ].sort((a, b) => a.set.startMs - b.set.startMs);
  const meters = sorted.map((o) => o.set.meters);
  const metersMin = Math.min(...meters);
  const metersMax = Math.max(...meters);
  const pools = new Set(sorted.map((o) => o.set.poolLengthMeters));
  const poolLengthMeters = pools.size === 1 ? sorted[0].set.poolLengthMeters : null;
  const basis: SetHistoryBasis =
    metersMin === metersMax && poolLengthMeters !== null ? "setTime" : "perLength";

  const series: SetHistoryPoint[] = [];
  for (const o of sorted) {
    const valueS = basis === "setTime" ? o.set.durationSeconds : meanCountedLength(o.set);
    if (valueS === null || !(valueS > 0)) continue;
    series.push({
      valueS,
      dateMs: o.set.startMs,
      workoutId: o.workoutId,
      meters: o.set.meters,
      hasPhantom: (o.set.ineligibleLaps ?? 0) > 0,
      strokes: pointStrokes(o.set),
      blocks: o.set.blocks ?? [],
    });
  }
  if (series.length === 0) return null;

  // The fastest tile skips an occurrence that contains a split-length glitch
  // (its time or average is built on a length that never happened). It still
  // draws and still counts. If every occurrence has one, the plain minimum
  // stands rather than an empty tile.
  const clean = series.filter((p) => !p.hasPhantom);
  const pool = clean.length > 0 ? clean : series;
  let fastest = pool[0];
  for (const p of pool) if (p.valueS < fastest.valueS) fastest = p; // strict: earliest keeps a tie

  // Best length per stroke: eligible at extraction, re-checked here against
  // the MATURE all-history median (a swim extracted first in a scan only had
  // its own laps to measure against — see fastestLengthEver).
  const best = new Map<string, number>();
  for (const o of sorted) {
    for (const [stroke, s] of Object.entries(o.set.perStroke)) {
      if (s.bestS === null) continue; // no record-eligible length of this stroke in this occurrence
      if (s.bestS < recordFloorSeconds(stroke, o.set.poolLengthMeters, medians)) continue;
      const cur = best.get(stroke);
      if (cur === undefined || s.bestS < cur) best.set(stroke, s.bestS);
    }
  }
  const strokeOrder = inImOrder(best.keys());
  const bestPerLengthByStroke = strokeOrder.map((stroke) => ({ stroke, seconds: best.get(stroke)! }));

  return {
    setId,
    displayName: display.displayName,
    timesSwum: series.length,
    basis,
    metersMin,
    metersMax,
    poolLengthMeters,
    isSignature: SET_DISPLAY[setId]?.isSignature ?? false,
    first: series[0],
    fastest,
    last: series[series.length - 1],
    series,
    bestPerLengthByStroke,
  };
}

/** One row per named set: signature (club-named) sets first, then generic shapes; within each, most-swum first. v1 carry-overs (sets: null) are skipped. */
export function setHistoryRows(
  entries: SwimFactsEntry[],
  medians: MedianByStroke = medianDurationByStroke(entries),
): SetHistoryRow[] {
  const bySet = new Map<string, Occurrence[]>();
  for (const e of entries) {
    if (e.sets === null) continue;
    for (const set of e.sets) {
      const list = bySet.get(set.setId) ?? [];
      list.push({ set, workoutId: e.swimUuid });
      bySet.set(set.setId, list);
    }
  }
  const rows: SetHistoryRow[] = [];
  for (const [setId, occ] of bySet) {
    const row = buildRow(setId, occ, medians);
    if (row) rows.push(row);
  }
  return rows.sort(
    (a, b) =>
      Number(b.isSignature) - Number(a.isSignature) ||
      b.timesSwum - a.timesSwum ||
      b.last.dateMs - a.last.dateMs,
  );
}

// ─── Copy helpers (one source of truth for the card, the sheet, and tests) ──

/** "4:31" for whole-set times, "37.1s" for average lengths. */
export function fmtSetValue(row: Pick<SetHistoryRow, "basis">, seconds: number): string {
  return row.basis === "setTime" ? fmtMSS(Math.round(seconds)) : `${fmtSeconds1(seconds)}s`;
}

/** "200 m" or "100–200 m". */
export function setMetersLabel(row: Pick<SetHistoryRow, "metersMin" | "metersMax">): string {
  return row.metersMin === row.metersMax
    ? `${row.metersMin} m`
    : `${row.metersMin}–${row.metersMax} m`;
}

/** "Sep 2, 2025" — the year always shows; a first swim can be a year back. */
export function fmtSetDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "7:12 AM" — when that swim of the set started. */
export function fmtSetTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** "Rockalita" from "The Rockalita" — reads right after "one". */
export function setShortName(displayName: string): string {
  return displayName.startsWith("The ") ? displayName.slice(4) : displayName;
}

/** The prefix in a set card's meta line, said once for all three tiles:
 *  "avg length · " when the tiles are average lengths, so none can be mistaken
 *  for a whole-set time (VP panel B7); "" for whole-set times. One term on every
 *  card, pool known or not (VP round 3: "avg 25" beside "avg length" read as two
 *  measures; a mixed-pool set cannot honestly say "avg 25"). */
export function setTileDatePrefix(row: Pick<SetHistoryRow, "basis">): string {
  return row.basis === "setTime" ? "" : "avg length · ";
}
